import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel,
    IconButton, Chip, Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PushPinIcon from '@mui/icons-material/PushPin';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useUser } from '../../context/userContext';
import NoticeDetailModal from '../dashboard/noticeDetailModal';
import { getAllNotices, createNotice, updateNotice, deleteNotice, togglePin, uploadNoticeImage } from '../../api/noticeApi';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const CATEGORY_COLORS = { General: '#94A3B8', Incentive: '#F59E0B', News: '#1A8CFF', Update: '#10B981' };

const EMPTY_FORM = {
    title: '', content: '', category: 'General', isPinned: false,
    attachmentUrl: null, attachmentName: null,
    startDate: '', endDate: '',
    showInSlideshow: false, slideshowImageUrl: null,
    youTubeUrl: '', type: 'Noticeboard',
};

const NoticeboardManagement = () => {
    const { user } = useUser();
    const [notices, setNotices] = useState([]);
    const [previewNotice, setPreviewNotice] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingNotice, setEditingNotice] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    const fetchNotices = async () => {
        try {
            const res = await getAllNotices();
            setNotices(res.data || []);
        } catch (err) { console.error(err); }
    };

    useEffect(() => { fetchNotices(); }, []);

    const handleOpen = (notice = null) => {
        if (notice) {
            setEditingNotice(notice);
            setForm({
                title: notice.title || '', content: notice.content || '',
                category: notice.category || 'General', isPinned: notice.isPinned || false,
                attachmentUrl: notice.attachmentUrl, attachmentName: notice.attachmentName,
                startDate: notice.startDate ? notice.startDate.split('T')[0] : '',
                endDate: notice.endDate ? notice.endDate.split('T')[0] : '',
                showInSlideshow: notice.showInSlideshow || false,
                slideshowImageUrl: notice.slideshowImageUrl,
                youTubeUrl: notice.youTubeUrl || '',
                type: notice.type || 'Noticeboard',
            });
        } else {
            setEditingNotice(null);
            setForm(EMPTY_FORM);
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.title.trim()) return alert('Title is required');
        try {
            const payload = {
                ...form,
                startDate: form.startDate || null,
                endDate: form.endDate || null,
            };
            if (editingNotice) {
                await updateNotice({ ...payload, noticeId: editingNotice.noticeId });
            } else {
                await createNotice(payload);
            }
            setDialogOpen(false);
            fetchNotices();
        } catch (err) { alert('Error saving notice'); }
    };

    const handleDelete = async (noticeId) => {
        if (!window.confirm('Delete this notice?')) return;
        await deleteNotice(noticeId);
        fetchNotices();
    };

    const handleTogglePin = async (noticeId) => {
        await togglePin(noticeId);
        fetchNotices();
    };

    const handleFileUpload = async (e, field) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            const res = await uploadNoticeImage(file);
            const fullUrl = (process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '') + res.data.url;
            setForm(prev => ({
                ...prev,
                [field]: fullUrl,
                ...(field === 'attachmentUrl' ? { attachmentName: file.name } : {}),
            }));
        } catch (err) { alert('Upload failed'); }
    };

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

    return (
        <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <Typography variant="h5" fontWeight={700}>Noticeboard Management</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ textTransform: 'none' }}>
                    Add Notice
                </Button>
            </div>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Title</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Pinned</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Start</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>End</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Published By</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Created</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {notices.map(n => (
                            <TableRow key={n.noticeId} hover>
                                <TableCell sx={{ fontSize: 12, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</TableCell>
                                <TableCell><Chip label={n.type || 'Noticeboard'} size="small" sx={{ fontSize: 10 }} /></TableCell>
                                <TableCell><Chip label={n.category} size="small" sx={{ fontSize: 10, bgcolor: CATEGORY_COLORS[n.category], color: 'white' }} /></TableCell>
                                <TableCell>{n.isPinned ? '📌' : '-'}</TableCell>
                                <TableCell sx={{ fontSize: 11 }}>{formatDate(n.startDate)}</TableCell>
                                <TableCell sx={{ fontSize: 11 }}>{formatDate(n.endDate)}</TableCell>
                                <TableCell sx={{ fontSize: 11 }}>{n.createdByName || '-'}</TableCell>
                                <TableCell sx={{ fontSize: 11 }}>{formatDate(n.createdOn)}</TableCell>
                                <TableCell>
                                    <IconButton size="small" onClick={() => setPreviewNotice(n)} title="View"><VisibilityIcon fontSize="small" sx={{ color: '#1A8CFF' }} /></IconButton>
                                    <IconButton size="small" onClick={() => handleOpen(n)}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => handleTogglePin(n.noticeId)}><PushPinIcon fontSize="small" color={n.isPinned ? 'primary' : 'disabled'} /></IconButton>
                                    <IconButton size="small" onClick={() => handleDelete(n.noticeId)}><DeleteIcon fontSize="small" color="error" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {notices.length === 0 && (
                            <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: '#94A3B8' }}>No notices yet</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingNotice ? 'Edit Notice' : 'Add Notice'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField label="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} size="small" required />
                    <TextField label="Description" value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} size="small" multiline rows={3} />
                    <div style={{ display: 'flex', gap: 12 }}>
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>Type</InputLabel>
                            <Select value={form.type} label="Type" onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                                <MenuItem value="Noticeboard">Noticeboard</MenuItem>
                                <MenuItem value="Slideshow">Slideshow</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>Category</InputLabel>
                            <Select value={form.category} label="Category" onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                                <MenuItem value="General">General</MenuItem>
                                <MenuItem value="Incentive">Incentive</MenuItem>
                                <MenuItem value="News">News</MenuItem>
                                <MenuItem value="Update">Update</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <TextField label="YouTube URL" value={form.youTubeUrl} onChange={e => setForm(f => ({ ...f, youTubeUrl: e.target.value }))} size="small" placeholder="https://youtube.com/watch?v=..." />
                    <div style={{ display: 'flex', gap: 12 }}>
                        <DatePicker label="Start Date" format="DD/MM/YYYY" value={form.startDate ? dayjs(form.startDate) : null} onChange={(val) => setForm(f => ({ ...f, startDate: val ? val.format('YYYY-MM-DD') : '' }))} slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }} />
                        <DatePicker label="End Date" format="DD/MM/YYYY" value={form.endDate ? dayjs(form.endDate) : null} onChange={(val) => setForm(f => ({ ...f, endDate: val ? val.format('YYYY-MM-DD') : '' }))} slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }} />
                    </div>
                    <div>
                        <Button variant="outlined" component="label" size="small" sx={{ mr: 1 }}>
                            Upload Photo
                            <input type="file" hidden accept="image/*" onChange={e => handleFileUpload(e, 'attachmentUrl')} />
                        </Button>
                        {form.attachmentName && <span style={{ fontSize: 12, color: '#64748B' }}>{form.attachmentName}</span>}
                    </div>
                    {form.type === 'Slideshow' && (
                        <div>
                            <Button variant="outlined" component="label" size="small">
                                Upload Slideshow Image
                                <input type="file" hidden accept="image/*" onChange={e => handleFileUpload(e, 'slideshowImageUrl')} />
                            </Button>
                            {form.slideshowImageUrl && <img src={form.slideshowImageUrl} alt="preview" style={{ maxWidth: 200, marginTop: 8, borderRadius: 6 }} />}
                        </div>
                    )}
                    <FormControlLabel control={<Switch checked={form.isPinned} onChange={e => setForm(f => ({ ...f, isPinned: e.target.checked }))} />} label="Pinned" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>{editingNotice ? 'Update' : 'Create'}</Button>
                </DialogActions>
            </Dialog>

            <NoticeDetailModal notice={previewNotice} open={!!previewNotice} onClose={() => setPreviewNotice(null)} />
        </div>
    );
};

export default NoticeboardManagement;
